import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { ExpandLess, ExpandMore, FilterList, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { ESTADOS_CLIENTE, ESTADOS_PORTAL } from "../hooks/useClientesFilters";
import {
  panelStyles,
  filterChipStyles,
  searchInputStyles,
  UI_COLORS,
} from "../styles/clientePortalStyles";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 0, 2, 0),
  },
  header: {
    ...panelStyles.header,
    marginBottom: 0,
  },
  headerExpanded: {
    borderRadius: "12px 12px 0 0",
    borderBottom: "none",
  },
  headerTitle: {
    fontWeight: 600,
    color: UI_COLORS.text.primary,
  },
  filterCount: {
    backgroundColor: "#1976D2",
    color: "#FFFFFF",
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  content: {
    ...panelStyles.content,
  },
  sectionLabel: {
    fontSize: "0.75rem",
    color: UI_COLORS.text.secondary,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    display: "block",
  },
  estadoChip: {
    ...filterChipStyles,
    cursor: "pointer",
  },
  searchField: {
    ...searchInputStyles,
  },
  activeChipsRow: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${UI_COLORS.borders.light}`,
  },
  resultCount: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${UI_COLORS.borders.light}`,
  },
}));

// ==================== COMPONENTE ====================

/**
 * Panel de filtros para el listado de clientes.
 *
 * @param {object}   filtros           - Estado actual de filtros
 * @param {function} updateFiltro      - Actualizar un filtro específico
 * @param {function} limpiarFiltros    - Limpiar todos los filtros
 * @param {function} removerFiltro     - Remover un filtro específico
 * @param {number}   filtrosActivos    - Cantidad de filtros activos
 * @param {boolean}  hayFiltrosActivos - Si hay filtros activos
 * @param {string[]} grupos            - Lista de grupos disponibles
 * @param {number}   totalClientes     - Total de clientes sin filtrar
 * @param {number}   totalFiltrados    - Total de clientes tras aplicar filtros
 */
export default function ClientesFilterPanel({
  filtros,
  updateFiltro,
  limpiarFiltros,
  removerFiltro,
  filtrosActivos,
  hayFiltrosActivos,
  grupos = [],
  totalClientes = 0,
}) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  // Estado local del input para aplicar debounce sin bloquear la escritura
  const [inputBusqueda, setInputBusqueda] = useState(filtros.busqueda);

  // Propagar al filtro real con 300 ms de debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      updateFiltro("busqueda", inputBusqueda);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputBusqueda, updateFiltro]);

  // Sincronizar el input cuando el filtro se limpia externamente
  useEffect(() => {
    setInputBusqueda(filtros.busqueda);
  }, [filtros.busqueda]);

  const mostrandoTodos = !hayFiltrosActivos;

  return (
    <Box className={classes.container}>
      {/* ── Header ── */}
      <Box
        className={`${classes.header} ${
          expanded ? classes.headerExpanded : ""
        }`}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FilterList sx={{ color: UI_COLORS.text.secondary, fontSize: 20 }} />
          <Typography variant="subtitle1" className={classes.headerTitle}>
            Filtros
          </Typography>
          {filtrosActivos > 0 && (
            <Chip
              size="small"
              label={`${filtrosActivos} activo${filtrosActivos !== 1 ? "s" : ""}`}
              className={classes.filterCount}
            />
          )}
        </Stack>

        <IconButton size="small" onClick={(e) => e.stopPropagation()}>
          {expanded ? (
            <ExpandLess sx={{ color: UI_COLORS.text.secondary }} />
          ) : (
            <ExpandMore sx={{ color: UI_COLORS.text.secondary }} />
          )}
        </IconButton>
      </Box>

      {/* ── Contenido colapsable ── */}
      <Collapse in={expanded}>
        <Box className={classes.content}>
          <Grid container spacing={2}>
            {/* Búsqueda */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="RUT, nombre, comuna, correo..."
                value={inputBusqueda}
                onChange={(e) => setInputBusqueda(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <Search
                      sx={{
                        mr: 0.5,
                        color: UI_COLORS.text.disabled,
                        fontSize: 20,
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            {/* Filtro por grupo */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={grupos}
                value={filtros.grupo || null}
                onChange={(_, newValue) =>
                  updateFiltro("grupo", newValue || "")
                }
                noOptionsText="Sin grupos"
                renderInput={(params) => (
                  <TextField {...params} label="Grupo" />
                )}
              />
            </Grid>

            {/* Estado del cliente */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography className={classes.sectionLabel}>
                Estado cliente
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {ESTADOS_CLIENTE.map((estado) => (
                  <Chip
                    key={estado.value}
                    label={estado.label}
                    size="small"
                    color={
                      filtros.estadoCliente === estado.value
                        ? estado.color
                        : "default"
                    }
                    variant={
                      filtros.estadoCliente === estado.value
                        ? "default"
                        : "outlined"
                    }
                    onClick={() =>
                      updateFiltro(
                        "estadoCliente",
                        filtros.estadoCliente === estado.value
                          ? ""
                          : estado.value,
                      )
                    }
                    className={classes.estadoChip}
                  />
                ))}
              </Stack>
            </Grid>

            {/* Estado portal */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography className={classes.sectionLabel}>
                Estado portal
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {ESTADOS_PORTAL.map((estado) => (
                  <Chip
                    key={estado.value}
                    label={estado.label}
                    size="small"
                    color={
                      filtros.estadoPortal === estado.value
                        ? estado.color
                        : "default"
                    }
                    variant={
                      filtros.estadoPortal === estado.value
                        ? "default"
                        : "outlined"
                    }
                    onClick={() =>
                      updateFiltro(
                        "estadoPortal",
                        filtros.estadoPortal === estado.value
                          ? ""
                          : estado.value,
                      )
                    }
                    className={classes.estadoChip}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>

          {/* ── Chips de filtros activos ── */}
          {hayFiltrosActivos && (
            <Box className={classes.activeChipsRow}>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={0.75}
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  sx={{ color: UI_COLORS.text.secondary, mr: 0.5 }}
                >
                  Activos:
                </Typography>

                {filtros.busqueda && (
                  <Chip
                    size="small"
                    label={`Búsqueda: "${filtros.busqueda}"`}
                    onDelete={() => removerFiltro("busqueda")}
                  />
                )}

                {filtros.grupo && (
                  <Chip
                    size="small"
                    label={`Grupo: ${filtros.grupo}`}
                    onDelete={() => removerFiltro("grupo")}
                  />
                )}

                {filtros.estadoCliente && (
                  <Chip
                    size="small"
                    label={
                      ESTADOS_CLIENTE.find(
                        (e) => e.value === filtros.estadoCliente,
                      )?.label
                    }
                    onDelete={() => removerFiltro("estadoCliente")}
                  />
                )}

                {filtros.estadoPortal !== "" && (
                  <Chip
                    size="small"
                    label={
                      ESTADOS_PORTAL.find(
                        (e) => e.value === filtros.estadoPortal,
                      )?.label
                    }
                    onDelete={() => removerFiltro("estadoPortal")}
                  />
                )}

                <Button
                  size="small"
                  onClick={limpiarFiltros}
                  sx={{ ml: 0.5, fontSize: "0.75rem" }}
                >
                  Limpiar todo
                </Button>
              </Stack>
            </Box>
          )}

          {/* ── Contador de resultados ── */}
          {totalClientes > 0 && (
            <Box className={classes.resultCount}>
              <Typography
                variant="caption"
                sx={{ color: UI_COLORS.text.secondary }}
              >
                {mostrandoTodos ? (
                  <>
                    <strong>{totalClientes}</strong> clientes en total
                  </>
                ) : (
                  <>
                    Mostrando <strong>{totalClientes}</strong> clientes
                    filtrados
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
